import { CreateClientPayload, Tag } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  archiveToPastClient,
  bulkAddClientsToProject,
  BulkAddClientsToProjectPayload,
  bulkAddClientsToSequence,
  BulkAddClientsToSequencePayload,
  bulkArchiveToPastClient,
  BulkArchiveToPastClientPayload,
  bulkCreateClients,
  BulkCreateClientsPayload,
  BulkDemoteProspectsPayload,
  bulkDemoteToProspect,
  bulkDestroyClients,
  bulkRenameClients,
  BulkRenameClientsPayload,
  bulkUpdateClients,
  BulkUpdateClientsPayload,
  createClient,
  updateClient,
  UpdateClientPayload,
} from "./api"

interface CreateClientMutationParams {
  client: CreateClientPayload
  overrideBookId?: string
}

export function useCreateClient() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ client, overrideBookId }: CreateClientMutationParams) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return createClient(context, client, overrideBookId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
    },
  })
}

export function useUpdateClient(clientId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: UpdateClientPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateClient(context, clientId, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["client", clientId] })
    },
  })
}

export function useBulkUpdateClients() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkUpdateClientsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkUpdateClients(context, params)
    },
    onMutate: async (params: BulkUpdateClientsPayload) => {
      await queryClient.cancelQueries({ queryKey: ["clients"] })
      const infiniteClientQueries = {
        predicate: (query: { queryKey: readonly unknown[] }) =>
          query.queryKey[0] === "clients" && query.queryKey.includes("infinite"),
      }

      // Store previous data for all matching queries
      const previousQueries = queryClient.getQueriesData(infiniteClientQueries)

      // Get the tag from cache if we're adding a tag
      let tagToAdd: Tag | undefined
      if (params.tag_id) {
        const tagsData = queryClient.getQueryData<{ tags: Tag[] }>(["tags", "household"])
        tagToAdd = tagsData?.tags?.find((tag) => tag.id === params.tag_id)
      }

      // Update all matching infinite queries
      queryClient.setQueriesData(infiniteClientQueries, (old: unknown) => {
        if (!old || typeof old !== "object" || !("pages" in old)) return old

        const oldData = old as {
          pages: Array<{ clients: Array<{ id: string; tier?: string; tags?: Tag[] }> }>
        }
        return {
          ...old,
          pages: oldData.pages.map((page) => ({
            ...page,
            clients: page.clients.map((client) => {
              if (params.client_ids.includes(client.id)) {
                const updates: Record<string, unknown> = {}
                if (params.next_review_due_on_override !== undefined) {
                  updates.next_review_due_on_override = params.next_review_due_on_override
                  updates.next_review_due_on = params.next_review_due_on_override
                }
                if (params.tier !== undefined) updates.tier = params.tier
                if (params.custom_field_key) {
                  const v = params.custom_field_value
                  const isComplexValue = typeof v === "string" && (v.startsWith("{") || v.startsWith("<"))
                  if (!isComplexValue) updates[params.custom_field_key] = v
                }
                if (tagToAdd) {
                  const existingTags = client.tags || []
                  const alreadyHasTag = existingTags.some((t) => t.id === tagToAdd.id)
                  if (!alreadyHasTag) {
                    updates.tags = [...existingTags, tagToAdd]
                  }
                }
                if (params.remove_tag_id) {
                  updates.tags = (client.tags || []).filter((tag) => tag.id !== params.remove_tag_id)
                }
                return { ...client, ...updates }
              }
              return client
            }),
          })),
        }
      })

      return { previousQueries }
    },
    onError: (err, variables, context) => {
      // Restore all previous queries on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    onSuccess: () => {
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["clients"] }), 5000)
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["clients"] }), 15000)
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["clients"] }), 60000)
    },
  })
}

export function useBulkCreateClients() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkCreateClientsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkCreateClients(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["lists"] })
    },
  })
}

export function useBulkDestroyClients() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (clientIds: string[]) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkDestroyClients(context, clientIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
    },
  })
}

export function useBulkAddClientsToProject() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkAddClientsToProjectPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkAddClientsToProject(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["projects"] })
    },
  })
}

export function useBulkAddClientsToSequence() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkAddClientsToSequencePayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkAddClientsToSequence(context, params)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["sequence-enrollments", variables.sequence_id] })
      queryClient.invalidateQueries({ queryKey: ["sequence-enrollment-stats"] })
    },
  })
}

export function useBulkDemoteClients() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkDemoteProspectsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkDemoteToProspect(context, params)
    },
    onSuccess: () => {
      // Invalidate both clients and prospects since demotion moves them
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function useArchiveClientToPastClient() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (clientId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return archiveToPastClient(context, clientId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["past_clients"] })
    },
  })
}

export function useBulkArchiveClientsToPastClient() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkArchiveToPastClientPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkArchiveToPastClient(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["past_clients"] })
    },
  })
}

export function useBulkRenameClients() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkRenameClientsPayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkRenameClients(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] })
    },
  })
}
